import React, { useEffect, useState } from "react";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";
import { Plug, CheckCircle2, AlertTriangle } from "lucide-react";
import {
  useIntegrationParameterPreview,
  IntegrationPreviewResult,
} from "../../lib/api";

/**
 * Review-step panel that previews the integration parameters in the selected checklists and shows,
 * per parameter, its configuration (with/without ontology objects, auto-initialized calc/number
 * params) and whether each referenced ontology object resolves on the target instance.
 */
interface Props {
  checklistIds: string[];
  serviceId: string;
  facilityId: string;
  targetServiceId?: string;
  targetFacilityId?: string;
  /**
   * Called when the preview finds blocking issues — properties/relations referenced by the
   * checklists but missing on the target, or referenced OBJECTS (e.g. SRS branching rule inputs)
   * that don't resolve there. The parent disables Confirm while true — the backend hard-blocks
   * these imports anyway; surfacing them here just moves the failure before the Confirm click.
   */
  onBlockingIssues?: (blocked: boolean) => void;
}

const IntegrationParameterPreview: React.FC<Props> = ({
  checklistIds,
  serviceId,
  facilityId,
  targetServiceId,
  targetFacilityId,
  onBlockingIssues,
}) => {
  const { previewIntegrationParameters } = useIntegrationParameterPreview();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<IntegrationPreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!checklistIds.length || !serviceId || !facilityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    previewIntegrationParameters({ checklistIds, serviceId, facilityId, targetServiceId, targetFacilityId })
      .then((r) => {
        if (!cancelled) {
          setResult(r);
          setError(null);
          const missingObjects = (r.missingObjectsByChecklist ?? []).some((g) => g.missing.length > 0);
          onBlockingIssues?.((r.missingOntologyDependencies?.length ?? 0) > 0 || missingObjects);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Preview failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistIds.join(","), serviceId, facilityId, targetServiceId, targetFacilityId]);

  const header = (
    <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
      <Plug className="h-4 w-4" /> Integration parameters
    </h3>
  );

  if (loading) {
    return (
      <div className="mt-8">
        {header}
        <p className="text-sm text-muted-foreground">Analyzing integration parameters…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="mt-8">
        {header}
        <p className="text-sm text-red-600">Integration parameter preview failed: {error}</p>
      </div>
    );
  }
  const missingDependencies = result?.missingOntologyDependencies ?? [];
  const missingSystemProps = missingDependencies.filter((d) => d.systemProperty);
  const missingNonSystem = missingDependencies.filter((d) => !d.systemProperty);
  // Per-checklist attribution when the server provides it; fall back to one flat group.
  const missingGroups =
    result?.missingOntologyDependenciesByChecklist?.length
      ? result.missingOntologyDependenciesByChecklist
      : missingDependencies.length > 0
        ? [{ checklistId: "", checklistName: "", missing: missingDependencies }]
        : [];

  const dependencyItem = (d: (typeof missingDependencies)[number], i: number) => (
    <li key={`${d.entityId ?? i}`} className="font-mono">
      {d.objectTypeName ? `${d.objectTypeName}.` : ""}
      {d.displayName || d.externalId || d.entityId}
      {d.externalId && d.displayName ? ` (${d.externalId})` : ""}
      {d.kind === "RELATION" ? (
        <Badge variant="outline" className="ml-2 bg-red-100 text-red-800 border-red-200">
          Relation
        </Badge>
      ) : null}
    </li>
  );

  // Grouped list: one sub-block per checklist so a miss is never shown against a batch sibling.
  const groupedDependencyList = (filter: (d: (typeof missingDependencies)[number]) => boolean) => (
    <div className="mt-2 space-y-2">
      {missingGroups
        .map((g) => ({ ...g, missing: g.missing.filter(filter) }))
        .filter((g) => g.missing.length > 0)
        .map((g) => (
          <div key={g.checklistId || "all"}>
            {g.checklistName && (
              <div className="text-sm font-medium text-red-900">{g.checklistName}</div>
            )}
            <ul className="mt-1 space-y-1 text-sm text-red-800">{g.missing.map(dependencyItem)}</ul>
          </div>
        ))}
    </div>
  );

  // Blocking alert: referenced objects (SRS branching rule inputs, pinned integration objects, …)
  // that don't resolve on the target. Same predicate as the confirm-time hard gate — shown here so
  // the user learns about it at review, not after clicking Confirm. Create the objects on the
  // target (Ontology + Objects migration) first, then retry.
  const missingObjectGroups = (result?.missingObjectsByChecklist ?? []).filter((g) => g.missing.length > 0);
  const missingObjectsAlert = missingObjectGroups.length > 0 && (
    <div className="mt-8 rounded-lg border border-red-200 bg-red-50 p-4">
      <div className="flex items-center gap-2 font-semibold text-red-800">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Missing objects on target — migration blocked
      </div>
      <p className="mt-1 text-sm text-red-700">
        The selected checklist(s) reference objects (e.g. in Resource-parameter branching rules)
        that don't exist on the target. Migrate them via the Ontology Migration flow with
        "Include objects", or create them on the target, then retry.
      </p>
      <div className="mt-2 space-y-2">
        {missingObjectGroups.map((g) => (
          <div key={g.checklistId || "all"}>
            {g.checklistName && (
              <div className="text-sm font-medium text-red-900">{g.checklistName}</div>
            )}
            <ul className="mt-1 space-y-1 text-sm text-red-800">
              {g.missing.map((m, i) => (
                <li key={m.sourceObjectId ?? i} className="font-mono">
                  {m.sourceExternalId || m.sourceObjectId}
                  {m.objectTypeExternalId ? ` (${m.objectTypeExternalId})` : ""}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );

  // Advisory (non-blocking): ObjectTypes queried by RESOURCE/INTEGRATION parameters that aren't on
  // the target. The process import never migrates an ObjectType's objects (only pinned ones), so the
  // parameter would land with an empty picker — the objects must come across via the Ontology
  // Migration flow ("Include objects"). Amber, not red: the import can still proceed.
  const resourceOtGroups = (result?.resourceObjectTypesByChecklist ?? []).filter(
    (g) => g.objectTypes.length > 0
  );
  const resourceObjectTypesAdvisory = resourceOtGroups.length > 0 && (
    <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-center gap-2 font-semibold text-amber-800">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Resource parameters query ObjectTypes not on the target
      </div>
      <p className="mt-1 text-sm text-amber-700">
        The parameter(s) below let users pick objects from an ObjectType that doesn't exist on the
        target. The process import does not migrate an ObjectType's objects — migrate each ObjectType
        and its objects via the Ontology Migration flow with "Include objects", then import this
        process, or the parameter will have no values on the target.
      </p>
      <div className="mt-2 space-y-2">
        {resourceOtGroups.map((g) => (
          <div key={g.checklistId || "all"}>
            {g.checklistName && (
              <div className="text-sm font-medium text-amber-900">{g.checklistName}</div>
            )}
            <ul className="mt-1 space-y-1 text-sm text-amber-800">
              {g.objectTypes.map((o, i) => (
                <li key={o.objectTypeId ?? i} className="font-mono">
                  {o.parameterLabel ? `${o.parameterLabel}: ` : ""}
                  {o.objectTypeName || o.objectTypeExternalId || o.objectTypeId}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );

  // Blocking alerts: properties/relations referenced by the checklists but missing on the target.
  // Rendered independently of integration parameters — the backend rejects the import on these.
  // SYSTEM properties can't be created by the MU at all; the rest are healed by running the
  // Ontology Migration flow first.
  const missingDependencyAlerts = missingDependencies.length > 0 && (
    <>
      {missingSystemProps.length > 0 && (
        <div className="mt-8 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 font-semibold text-red-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Missing system properties on target — migration blocked
          </div>
          <p className="mt-1 text-sm text-red-700">
            The selected checklist(s) reference SYSTEM properties that don't exist on the target
            ObjectType. The Migration Utility cannot create system properties — migrate/create them on
            the target first, then retry.
          </p>
          {groupedDependencyList((d) => d.systemProperty)}
        </div>
      )}
      {missingNonSystem.length > 0 && (
        <div className="mt-8 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 font-semibold text-red-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Missing properties/relations on target — migration blocked
          </div>
          <p className="mt-1 text-sm text-red-700">
            The selected checklist(s) reference properties or relations that don't exist on the
            target ObjectType. Migrate these via the Ontology Migration flow first, then retry.
          </p>
          {groupedDependencyList((d) => !d.systemProperty)}
        </div>
      )}
    </>
  );

  // When the selected checklists have no integration parameters, there's nothing to preview —
  // render only the (possible) missing-dependency alerts so the review step skips the
  // integration-parameter section entirely.
  if (!result || result.integrationParameterCount === 0) {
    return (
      <>
        {missingObjectsAlert}
        {missingDependencyAlerts}
        {resourceObjectTypesAdvisory}
      </>
    );
  }

  return (
    <div className="mt-8">
      {missingObjectsAlert}
      {missingDependencyAlerts}
      {resourceObjectTypesAdvisory}
      {header}
      <p className="text-sm text-muted-foreground mb-4">
        {result.integrationParameterCount} integration parameter(s).{" "}
        {result.unresolvedObjectCount > 0 ? (
          <span className="text-orange-600 font-medium">
            {result.unresolvedObjectCount} of {result.objectReferenceCount} referenced object(s) will NOT
            resolve on the target.
          </span>
        ) : (
          <span className="text-green-700">All referenced objects resolve on the target.</span>
        )}
      </p>

      <div className="space-y-3">
        {result.integrationParameters.map((p) => (
          <div key={p.parameterId} className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-foreground truncate">{p.label || p.parameterId}</div>
                <div className="text-xs text-muted-foreground">
                  ObjectType: {p.objectTypeExternalId || p.objectTypeId || "—"}
                  {p.checklistName ? <> · Checklist: {p.checklistName}</> : null}
                </div>
              </div>
              <div className="flex flex-wrap gap-1 shrink-0 justify-end">
                <Badge variant="outline">
                  {p.hasOntologyObjects ? "With ontology objects" : "No pinned objects"}
                </Badge>
                {p.autoInitCalculationParamIds.length > 0 && (
                  <Badge variant="outline">{p.autoInitCalculationParamIds.length} calc</Badge>
                )}
                {p.autoInitNumberParamIds.length > 0 && (
                  <Badge variant="outline">{p.autoInitNumberParamIds.length} number</Badge>
                )}
              </div>
            </div>

            {p.objectReferences.length > 0 && (
              <div className="mt-3 space-y-1">
                {p.objectReferences.map((ref) => (
                  <div key={ref.sourceObjectId} className="flex items-center gap-2 text-sm">
                    {ref.resolved ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" />
                    )}
                    <span className="text-muted-foreground truncate">
                      {ref.sourceExternalId || ref.sourceObjectId}
                    </span>
                    <Badge
                      variant="outline"
                      className={cn(
                        ref.resolved
                          ? "bg-green-100 text-green-800 border-green-200"
                          : "bg-orange-100 text-orange-800 border-orange-200"
                      )}
                    >
                      {ref.resolved ? "Resolved" : "Missing on target"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IntegrationParameterPreview;
